import React from 'react';

import { Box, Badge as UCLBadge } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { Tabs } from 'expo-router';
import { useIntl } from 'react-intl';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { StandardMobileHeader } from 'components/app-header/mobile-header';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logRBIEvent,
} from 'state/amplitude';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { primitive } from 'styles/constants/primitives';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { isIos } from 'utils/environment';

export const unstable_settings = {
  initialRouteName: 'index',
};

export default function TabsLayout() {
  const { formatMessage } = useIntl();
  const isDesktopWeb = useIsDesktopWebBreakpoint();

  const renderTabLabel = (id: string) => ({ focused }: { focused: boolean }) => (
    <Text
      style={[
        focused
          ? { fontFamily: 'Flame-Regular', color: primitive.bk.$bbqBrown }
          : { fontFamily: 'Flame-RegularSans', color: '#82685B' }, // lighter BBQ brown that is a11y on the mayoWhite background
        tabBarStyles.tabBarLabelStyle,
      ]}
    >
      {formatMessage({ id })}
    </Text>
  );

  return (
    <Tabs
      sceneContainerStyle={{ backgroundColor: primitive.bk.$mayoEggWhite }}
      screenOptions={{
        header: WrappedStandardMobileHeader,
        tabBarButton: TouchFeedbackTabBarIcon,
        tabBarStyle: tabBarStyles.tabBarStyle,
        tabBarLabelStyle: tabBarStyles.tabBarLabelStyle,
        tabBarLabelPosition: 'below-icon',
      }}
    >
      <Tabs.Screen
        name="index"
        {...getAnalyticsEvent('Home', '/')}
        options={{
          header: () => <StandardMobileHeader hideTsm={!isDesktopWeb} />,
          title: formatMessage({ id: 'navHome' }),
          tabBarTestID: getTestId('Home'),
          tabBarLabel: renderTabLabel('navHome'),
          tabBarIcon: ({ focused }) => <TabBarIcon name="home" focused={focused} altId="navHome" />,
        }}
      />
      <Tabs.Screen
        name="menu"
        {...getAnalyticsEvent('Menu', '/menu')}
        options={{
          title: formatMessage({ id: 'navMenu' }),
          tabBarTestID: getTestId('Menu'),
          tabBarLabel: renderTabLabel('navMenu'),
          headerShown: false,
          tabBarIcon: ({ focused }) => <TabBarIcon name="menu" focused={focused} altId="navMenu" />,
        }}
      />
      <Tabs.Screen
        name="my-code"
        {...getAnalyticsEvent('My Code', '/my-code')}
        options={{
          title: formatMessage({ id: 'navMyCode' }),
          tabBarTestID: getTestId('My-Code'),
          tabBarItemStyle: {
            display: isDesktopWeb ? 'none' : 'flex',
          },
          tabBarLabel: renderTabLabel('navMyCode'),
          tabBarIcon: ({ focused }) => (
            <TabBarIcon
              name="myCode"
              focused={focused}
              children={<MyCodeBadge />}
              altId="navMyCode"
            />
          ),
        }}
      />
      <Tabs.Screen
        name="offers"
        {...getAnalyticsEvent('Offers', '/offers')}
        options={{
          title: formatMessage({ id: 'navOffers' }),
          tabBarTestID: getTestId('Offers'),
          tabBarLabel: renderTabLabel('navOffers'),
          tabBarIcon: ({ focused }) => (
            <TabBarIcon name="offers" focused={focused} altId="navOffers" />
          ),
        }}
      />
      <Tabs.Screen
        name="rewards"
        {...getAnalyticsEvent('Rewards', '/rewards')}
        options={{
          title: formatMessage({ id: 'navRewards' }),
          tabBarTestID: getTestId('Rewards'),
          tabBarLabel: renderTabLabel('navRewards'),
          tabBarIcon: ({ focused }) => (
            <TabBarIcon name="rewards" focused={focused} altId="navRewards" />
          ),
        }}
      />
    </Tabs>
  );
}

const getTestId = (name: string) => `nav-with-picture-icon-${name}`;

const getAnalyticsEvent = (name: string, route: string) => ({
  listeners: {
    tabPress: () => {
      //this used to use logButtonClick with logDuplicateClickEvent but dupes seem to be no longer necessary. add back if complaints
      logRBIEvent({
        name: CustomEventNames.CLICK_EVENT,
        type: EventTypes.Other,
        attributes: {
          text: name,
          component: ClickEventComponentNames.NAV_BAR,
          componentId: route,
        },
      });
    },
  },
});

const TouchFeedbackTabBarIcon = (props: any) => <TouchableOpacity activeOpacity={0.3} {...props} />;

// for some reason this needs to be wrapped this way. don't change it.
const WrappedStandardMobileHeader = () => <StandardMobileHeader />;

const TAB_ICONS = {
  home: {
    base: require('../../assets/images/nav/home.svg'),
    active: require('../../assets/images/nav/home-active.svg'),
  },
  menu: {
    base: require('../../assets/images/nav/menu.svg'),
    active: require('../../assets/images/nav/menu-active.svg'),
  },
  myCode: {
    base: require('../../assets/images/nav/my-code.svg'),
    active: require('../../assets/images/nav/my-code-active.svg'),
  },
  offers: {
    base: require('../../assets/images/nav/offers.svg'),
    active: require('../../assets/images/nav/offers-active.svg'),
  },
  rewards: {
    base: require('../../assets/images/nav/rewards.svg'),
    active: require('../../assets/images/nav/rewards-active.svg'),
  },
};

const TabBarIcon: React.FC<React.PropsWithChildren<{
  name: string;
  focused: boolean;
  altId: string;
}>> = ({ name, focused, children, altId }) => {
  const { formatMessage } = useIntl();
  const alt = formatMessage({ id: altId });
  return (
    <View style={tabBarStyles.iconWrapper}>
      <View
        style={[tabBarStyles.activeIndicatorContainer, focused && tabBarStyles.activeIndicator]}
      />
      <Image
        alt={alt}
        source={focused ? TAB_ICONS[name].active : TAB_ICONS[name].base}
        style={{ height: 22, width: 22 }}
        {...hiddenAccessibilityPlatformProps}
      />
      {children}
    </View>
  );
};

const MyCodeBadge = () => {
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();
  const count = inRestaurantRedemptionCart.reduce(
    (acc, currentValue) => acc + currentValue.quantity,
    0
  );

  if (!count) {
    return null;
  }

  return (
    <BadgeOuter>
      <BadgeInner variant="number-solid">{count.toString()}</BadgeInner>
    </BadgeOuter>
  );
};

const BadgeOuter = Box.withConfig({
  position: 'absolute',
  zIndex: 1,
  top: -5,
  right: 5,
});

const BadgeInner = UCLBadge.withConfig({
  bgColor: primitive.bk.$meltyYellow,
  _text: {
    fontFamily: 'heading',
    fontSize: 'xs',
    color: primitive.bk.$bbqBrown,
  },
  width: '$5',
  height: '$5',
  style: {
    minWidth: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
});

export const tabBarStyles = StyleSheet.create({
  tabBarStyle: {
    backgroundColor: primitive.bk.$mayoEggWhite,
    // web/android tab bar needs a little more space
    ...(!isIos
      ? {
          height: 55,
          paddingBottom: 6,
        }
      : null),
    borderTopColor: '#FFFFFF',
    borderTopWidth: 1,
  },
  tabBarLabelStyle: {
    fontSize: 12,
  },
  iconWrapper: {
    alignItems: 'center',
  },
  activeIndicatorContainer: {
    marginTop: -6,
    marginBottom: 4,
    height: 3,
  },
  activeIndicator: {
    backgroundColor: primitive.bk.$bbqBrown,
    width: 70,
  },
});
